/**
 * How a month's callbacks compare with the month before it.
 *
 * More callbacks is worse, so `higher` is the red one: direction and colour point
 * the same way, the opposite of how a maintenance figure reads.
 */
export type CallbackTrend = 'higher' | 'lower' | 'same';

export const trendBetween = (callbacks: number, previous: number | undefined): CallbackTrend => {
  // The month before the window is outside what was asked for, so the first column
  // claims no trend rather than reading the absence as zero.
  if (previous === undefined) return 'same';
  if (callbacks > previous) return 'higher';
  return callbacks < previous ? 'lower' : 'same';
};

/**
 * Which months this elevator was already past its contracted year on.
 *
 * The window is always a year, so the allowance is a budget the running total is
 * spent against. Once it is gone it stays gone: a quieter month later does not buy
 * the year back, and must not read as though it did.
 */
export const overAllowanceFrom = (
  callbacks: readonly number[],
  emergency: readonly number[],
  target: number,
  emergencyTarget: number,
): boolean[] => {
  let spent = 0;
  let spentEmergency = 0;
  return callbacks.map((count, index) => {
    spent += count;
    spentEmergency += emergency[index] ?? 0;
    return spent > target || spentEmergency > emergencyTarget;
  });
};

export const trendColor = (trend: CallbackTrend): string =>
  ({ higher: 'error.main', lower: 'success.main', same: 'text.primary' })[trend];

export const countLabel = (callbacks: number): string => {
  if (callbacks === 0) return 'no callbacks';
  return callbacks === 1 ? '1 callback' : `${callbacks} callbacks`;
};

/** What the contract allows, one line per rate, in the `≤ n` form the contract page uses. */
export const requirementLines = (target: number, emergencyTarget: number): [string, string] => [
  `≤${target} callbacks`,
  `≤${emergencyTarget} emergency`,
];

/** The same two lines as one string, for the tooltip, the export and the quick filter. */
export const requirementLabel = (target: number, emergencyTarget: number): string =>
  requirementLines(target, emergencyTarget).join(' · ');

/**
 * Which of the two contracted rates this elevator has broken, if either.
 *
 * Named separately rather than flagged as one "over target": an elevator inside its
 * callback allowance can still be trapping people too often, and a consultant chases
 * those two differently.
 */
export const overTargetLabel = (
  carName: string,
  overTarget: boolean,
  overEmergencyTarget: boolean,
): string | null => {
  if (overTarget && overEmergencyTarget) {
    return `${carName} is over its contracted callback and emergency rates`;
  }
  if (overTarget) return `${carName} is over its contracted callback rate`;
  return overEmergencyTarget ? `${carName} is over its contracted emergency rate` : null;
};
